<template>
  <div class="c-price-entry">
    <v-form-number
      type="price"
      :label="label"
      :disabled="disabled"
      :value="+value || ''"
      :step="step"
      :min="min"
      :max="max"
      @input="onInput"
    >
      <template #prepend>
        <v-dropdown :class="[lock]" :disabled="disabled" no-caret>
          <template v-slot:button-content>
            <span class="sr-only">{{ label }}</span>
            <font-awesome-icon class="fa-sm" :icon="['fas', `${lockIcon}`]" />
          </template>
          <v-dropdown-item v-for="(type, index) in lockTypes" :key="index" @click="onLockSelected(type)">
            <span :class="[type.lock]">
              <font-awesome-icon class="fa-sm" :icon="['fas', `${type.icon}`]" />
            </span>
            <span>{{ type.name }}</span>
          </v-dropdown-item>
        </v-dropdown>
      </template>
      <template #append>
        <v-button
          v-if="netCreditDebitVisible"
          :class="netCreditDebit.toLowerCase()"
          :disabled="netCreditDebitDisabled"
          @click="onNetCreditDebitClicked"
        >
          <span role="presentation" aria-hidden="true">{{ netCreditDebit }}</span
          ><span class="sr-only">{{ screenReaderText }}</span>
        </v-button>
      </template>
    </v-form-number>
    <label v-if="isEstimatedVisible">{{ estimatedPrice }}</label>
  </div>
</template>

<script>
import { library as fontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { faLock as fasLock } from '@fortawesome/pro-regular-svg-icons/faLock';
import { faDollarSign as fasDollarSign } from '@fortawesome/pro-regular-svg-icons/faDollarSign';
import { faPercent as fasPercent } from '@fortawesome/pro-regular-svg-icons/faPercent';
fontAwesomeLibrary.add(fasLock, fasDollarSign, fasPercent);

export default {
  name: 'c-price-entry',
  props: {
    label: {
      type: String,
      default: ''
    },
    step: {
      type: Number,
      default: 0.1
    },
    value: {
      type: [Number, String],
      default: 0
    },
    min: {
      type: [Number, String],
      default: 0
    },
    max: {
      type: [Number, String],
      default: Number.MAX_VALUE
    },
    disabled: {
      type: Boolean,
      default: false
    },
    lock: {
      type: String,
      default: 'none'
    },
    lockTypes: {
      type: Array,
      required: true
    },
    estimatedPrice: {
      type: String,
      default: ''
    },
    netCreditDebit: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentValue: 0,
      errorMsg: '',
      disable: {
        decBtn: this.disabled,
        incBtn: false
      }
    };
  },
  computed: {
    isEstimatedVisible() {
      return this.estimatedPrice;
    },
    lockIcon() {
      if (this.lock === 'abs') {
        return 'dollar-sign';
      } else if (this.lock === 'rel') {
        return 'percent';
      } else {
        return 'lock';
      }
    },
    netCreditDebitVisible() {
      return this.netCreditDebit;
    },
    netCreditDebitDisabled() {
      return this.disabled || this.netCreditDebit === 'EV';
    },
    screenReaderText() {
      return this.netCreditDebit === 'CR'
        ? this.$t('white-label.widgets.orderEntry.strategy.netCreditButton')
        : this.$t('white-label.widgets.orderEntry.strategy.netDebitButton');
    }
  },
  methods: {
    onInput(value) {
      this.$emit('input', value);
      this.validateActionLock();
    },
    onLockSelected(type) {
      this.$emit('selected', { limit: type, value: this.currentValue, oldLock: this.lock });
    },
    onNetCreditDebitClicked() {
      if (this.netCreditDebit === 'CR') {
        this.$emit('net-credit-debit-changed', 'DR');
      } else if (this.netCreditDebit === 'DR') {
        this.$emit('net-credit-debit-changed', 'CR');
      }
      this.validateActionLock();
    },
    validateActionLock() {
      if (this.lock === 'bid' || this.lock === 'ask' || this.lock === 'mid' || this.lock === 'nat') {
        this.$emit('reset', { lock: 'none' });
      }
    }
  }
};
</script>

<style lang="scss">
@import '~@/styles/components/custom/c-price-entry';
</style>
